import type { CanvasItem } from "@bedrock/domain";

export interface CanvasFolderDropIntent {
  kind: "inside" | "outside";
  stackId: string;
  itemCount: number;
}

export function canStartCanvasItemDrag(target: Element): boolean {
  const interactiveTarget = target.closest("button, a, input, [contenteditable='true'], .bedrock-editor, .resize-handle");
  return interactiveTarget === null || interactiveTarget.classList.contains("item-clip");
}

export function canvasDragItems(items: CanvasItem[], activeIds: string[]): CanvasItem[] {
  const movingIds = new Set(activeIds);
  let addedChild = true;

  while (addedChild) {
    addedChild = false;
    for (const item of items) {
      if (item.parentStackId && movingIds.has(item.parentStackId) && !movingIds.has(item.id)) {
        movingIds.add(item.id);
        addedChild = true;
      }
    }
  }

  return items.filter((item) => movingIds.has(item.id));
}

function isContainer(item: CanvasItem): boolean {
  return item.type === "stack" || item.type === "frame";
}

function canNestInContainer(item: CanvasItem, container: CanvasItem): boolean {
  if (!isContainer(container) || item.type === "frame") return false;
  return item.type !== "stack" || container.type === "frame";
}

function containsItem(container: CanvasItem, item: CanvasItem): boolean {
  const center = { x: item.x + item.width / 2, y: item.y + item.height / 2 };
  const containerCenter = { x: container.x + container.width / 2, y: container.y + container.height / 2 };
  const radians = container.rotation * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const offset = { x: center.x - containerCenter.x, y: center.y - containerCenter.y };
  const localCenter = {
    x: offset.x * cosine + offset.y * sine,
    y: -offset.x * sine + offset.y * cosine
  };
  return Math.abs(localCenter.x) <= container.width / 2 && Math.abs(localCenter.y) <= container.height / 2;
}

function canvasDropTarget(item: CanvasItem, moved: CanvasItem, allItems: CanvasItem[], movingIds: ReadonlySet<string>): CanvasItem | undefined {
  const containers = allItems
    .filter((candidate) => canNestInContainer(item, candidate) && !movingIds.has(candidate.id) && containsItem(candidate, moved))
    .sort((first, second) => second.zIndex - first.zIndex);
  const containerIds = new Set(containers.map((candidate) => candidate.id));
  const nestedContainers = containers.filter((candidate) => candidate.parentStackId && containerIds.has(candidate.parentStackId));

  if (nestedContainers.length > 0) {
    return nestedContainers.find((candidate) => candidate.id === item.parentStackId) ?? nestedContainers[0];
  }
  return containers.find((candidate) => candidate.id === item.parentStackId) ?? containers[0];
}

export function reconcileFrameMembership(frame: CanvasItem, items: CanvasItem[]): CanvasItem[] {
  if (frame.props.kind !== "frame") return items;
  return items.map((item) => {
    if (item.id === frame.id || !canNestInContainer(item, frame)) return item;
    const inside = containsItem(frame, item);
    if (item.parentStackId === frame.id && !inside) return { ...item, parentStackId: null };
    if (item.parentStackId === null && inside) {
      return { ...item, parentStackId: frame.id, zIndex: Math.max(item.zIndex, frame.zIndex + 1) };
    }
    return item;
  });
}

export function includeFrameMembershipChanges(
  before: CanvasItem[],
  after: CanvasItem[],
  allItems: CanvasItem[],
  frameId: string
): { before: CanvasItem[]; after: CanvasItem[] } {
  const afterById = new Map(after.map((item) => [item.id, item]));
  const projected = allItems.map((item) => afterById.get(item.id) ?? item);
  const frame = projected.find((item) => item.id === frameId);
  if (!frame || frame.props.kind !== "frame") return { before, after };

  const reconciled = reconcileFrameMembership(frame, projected);
  const projectedById = new Map(projected.map((item) => [item.id, item]));
  const reconciledById = new Map(reconciled.map((item) => [item.id, item]));
  const changedMembershipIds = reconciled.filter((item) => {
    const previous = projectedById.get(item.id);
    return previous && (previous.parentStackId !== item.parentStackId || previous.zIndex !== item.zIndex);
  }).map((item) => item.id);
  const ids = [...new Set([...after.map((item) => item.id), ...changedMembershipIds])];
  const originalById = new Map(allItems.map((item) => [item.id, item]));
  const explicitBeforeById = new Map(before.map((item) => [item.id, item]));

  return {
    before: ids.flatMap((id) => {
      const item = explicitBeforeById.get(id) ?? originalById.get(id);
      return item ? [item] : [];
    }),
    after: ids.flatMap((id) => {
      const item = reconciledById.get(id) ?? afterById.get(id);
      return item ? [item] : [];
    })
  };
}

export function moveCanvasDragItems(before: CanvasItem[], allItems: CanvasItem[], dx: number, dy: number, primaryItemId: string): CanvasItem[] {
  const movingIds = new Set(before.map((item) => item.id));
  const movedItems = before.map((item) => ({ ...item, x: item.x + dx, y: item.y + dy }));
  const primaryIndex = before.findIndex((item) => item.id === primaryItemId);
  const primary = before[primaryIndex];
  const movedPrimary = movedItems[primaryIndex];
  const primaryTarget = primary && movedPrimary && !(primary.parentStackId && movingIds.has(primary.parentStackId))
    ? canvasDropTarget(primary, movedPrimary, allItems, movingIds)
    : undefined;
  const sharedFolderTarget = primaryTarget?.props.kind === "stack" ? primaryTarget : undefined;

  return before.map((item, index) => {
    const moved = movedItems[index]!;
    if (item.parentStackId && movingIds.has(item.parentStackId)) return moved;

    const targetStack = sharedFolderTarget && !isContainer(item)
      ? sharedFolderTarget
      : canvasDropTarget(item, moved, allItems, movingIds);

    return {
      ...moved,
      parentStackId: targetStack?.id ?? (item.parentStackId ? null : item.parentStackId),
      zIndex: targetStack?.type === "frame" ? Math.max(moved.zIndex, targetStack.zIndex + 1) : moved.zIndex
    };
  });
}

export function canvasFolderDropIntent(before: CanvasItem[], after: CanvasItem[], primaryItemId: string): CanvasFolderDropIntent | null {
  const previousPrimary = before.find((item) => item.id === primaryItemId);
  const nextById = new Map(after.map((item) => [item.id, item]));
  const nextPrimary = nextById.get(primaryItemId);
  if (!previousPrimary || !nextPrimary || previousPrimary.parentStackId === nextPrimary.parentStackId) return null;

  const kind = nextPrimary.parentStackId ? "inside" : "outside";
  const stackId = nextPrimary.parentStackId ?? previousPrimary.parentStackId;
  if (!stackId) return null;

  const itemCount = before.filter((item) => {
    const next = nextById.get(item.id);
    if (!next) return false;
    return kind === "inside"
      ? item.parentStackId !== stackId && next.parentStackId === stackId
      : item.parentStackId === stackId && next.parentStackId === null;
  }).length;

  return { kind, stackId, itemCount: Math.max(1, itemCount) };
}
